import React, { useState, useCallback, useRef } from 'react';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * DragDropUpload - Modern drag-and-drop file upload component
 */
const DragDropUpload = ({
    onUpload,
    accept = '*',
    maxSize = 10 * 1024 * 1024, // 10MB default
    multiple = false,
    uploading = false
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const validateFile = (file) => {
        if (file.size > maxSize) {
            return `File too large. Max size is ${Math.round(maxSize / 1024 / 1024)}MB`;
        }
        return null;
    };

    const handleFiles = useCallback((files) => {
        setError(null);
        const fileList = Array.from(files);

        // Validate files
        for (const file of fileList) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        if (multiple) {
            setSelectedFiles(prev => [...prev, ...fileList]);
        } else {
            setSelectedFiles(fileList.slice(0, 1));
        }
    }, [multiple, maxSize]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files?.length) {
            handleFiles(files);
        }
    }, [handleFiles]);

    const handleFileInput = (e) => {
        const files = e.target.files;
        if (files?.length) {
            handleFiles(files);
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (!selectedFiles.length || !onUpload) return;

        setUploadProgress(0);
        for (let i = 0; i < selectedFiles.length; i++) {
            await onUpload(selectedFiles[i]);
            setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
        }
        setSelectedFiles([]);
        setUploadProgress(0);
    };

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext)) return '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['xls', 'xlsx'].includes(ext)) return '📊';
        return '📎';
    };

    return (
        <div className="space-y-4">
            {/* Drop Zone */}
            <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                    relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer
                    transition-all duration-300
                    ${isDragging
                        ? 'border-indigo-500 bg-indigo-50 scale-[1.02]'
                        : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleFileInput}
                    className="hidden"
                />

                <div className={`
                    w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center
                    transition-all duration-300
                    ${isDragging ? 'bg-indigo-100 text-indigo-600 scale-110' : 'bg-slate-100 text-slate-400'}
                `}>
                    <CloudArrowUpIcon className="h-8 w-8" />
                </div>

                <p className="text-sm font-medium text-slate-700 mb-1">
                    {isDragging ? 'Drop your files here' : 'Drag & drop files here'}
                </p>
                <p className="text-xs text-slate-500">
                    or <span className="text-indigo-600 font-medium">browse</span> to upload
                </p>
                <p className="text-xs text-slate-400 mt-2">
                    Max file size: {Math.round(maxSize / 1024 / 1024)}MB
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                    <XMarkIcon className="h-4 w-4" />
                    {error}
                </div>
            )}

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
                <div className="space-y-2">
                    {selectedFiles.map((file, idx) => (
                        <div
                            key={idx}
                            className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200"
                        >
                            <span className="text-xl">{getFileIcon(file.name)}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                                <p className="text-xs text-slate-500">
                                    {(file.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    ))}

                    {/* Upload Button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); handleUpload(); }}
                        disabled={uploading}
                        className={`
                            w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-200
                            ${uploading
                                ? 'bg-slate-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:shadow-lg hover:-translate-y-0.5'}
                        `}
                    >
                        {uploading ? (
                            <span className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Uploading... {uploadProgress}%
                            </span>
                        ) : (
                            `Upload ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default DragDropUpload;
